import { SendOutlined } from "@ant-design/icons";
import { Button, Flex, type TableColumnsType, Tooltip } from "antd";
import type { MouseEvent } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { StyledButton } from "~Common/Components";
import PermissionCheck from "~Common/PermissionCheck";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import type { UserStatuses } from "../enums/userStatuses";
import type { UserTransformed } from "../types";

const useGetPermissionUsersTableColumns = (
  onDeleteUser: (event: MouseEvent, userId: number) => void,
  onInviteNotify: (event: MouseEvent, user: UserTransformed) => void,
) => {
  const { formatMessage } = useIntl();

  const columns: TableColumnsType<UserTransformed> = [
    {
      title: formatMessage({ id: "permissions.user.name" }),
      dataIndex: "name",
      width: 300,
      defaultSortOrder: "ascend",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: formatMessage({ id: "email" }),
      dataIndex: "email",
      width: 300,
      sorter: (a, b) => a.email.localeCompare(b.email),
    },
    {
      title: formatMessage({ id: "permissions.user.role" }),
      dataIndex: "userRolesStr",
    },
    {
      title: formatMessage({ id: "permissions.user.status" }),
      dataIndex: "status",
      width: 100,
      render: (value: UserStatuses) => formatMessage({ id: `permissions.user.status.${value}` }),
    },
    {
      align: "center",
      key: "actions",
      width: 120,
      render: (_, user: UserTransformed) => (
        <Flex gap={8}>
          <StyledButton size="small" type="text">
            <Link to={`manage/${user.userId}`}>
              <FormattedMessage id="manage" />
            </Link>
          </StyledButton>
          <PermissionCheck permissionKeys={[PermissionNames.PERMISSIONS]} permissionOptions={[PermissionOperations.DELETE]}>
            <Tooltip title={formatMessage({ id: "delete" })}>
              <Button
                onClick={(event: MouseEvent) => onDeleteUser(event, user.userId)}
                type="text"
                size="small"
                icon={<i className="fas fa-trash-can mr-2" />}
              />
            </Tooltip>
          </PermissionCheck>
          <PermissionCheck permissionKeys={[PermissionNames.PERMISSIONS]} permissionOptions={[PermissionOperations.CREATE]}>
            <Tooltip title={formatMessage({ id: user.isPending ? "invite" : "notify" })}>
              <Button onClick={(event: MouseEvent) => onInviteNotify(event, user)} type="text" size="small" icon={<SendOutlined />} />
            </Tooltip>
          </PermissionCheck>
        </Flex>
      ),
    },
  ];

  return { columns };
};

export default useGetPermissionUsersTableColumns;
